import type { ActiveEntityFocus, ChatSubjectFocusEntry } from '#types/types';
import {
  isEntityFocusSubject,
  normalizeSubjectFocus,
  toSubjectFocusHistoryEntry,
} from '#lib/leasing/session-focus';

type ChatSessionProjectLike = {
  activeSubject?: unknown;
  subjectHistory?: unknown;
  projectId?: unknown;
  // Immutable creation scope ("agents live in projects"): a studio-scoped
  // session's home Project id, used as the last-resort project resolution when
  // the subject has been cleared but the session still belongs to the project.
  creationModule?: unknown;
  creationEntityType?: unknown;
  creationEntityId?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const coerceActiveSubject = (value: unknown): ActiveEntityFocus | null => {
  if (!isRecord(value)) return null;
  return normalizeSubjectFocus(value);
};

const resolveProjectIdFromProjectSubject = (subject: unknown): string | null => {
  const coerced = coerceActiveSubject(subject);
  if (!coerced || !isEntityFocusSubject(coerced) || coerced.entityType !== 'Project') return null;
  return coerced.entityId;
};

const normalizeSubjectHistory = (value: unknown): ChatSubjectFocusEntry[] | undefined => {
  if (!Array.isArray(value)) return undefined;

  // The history entry is rebuilt by the canonical pair (normalizeSubjectFocus +
  // toSubjectFocusHistoryEntry), the same one mappers.ts and chat-sessions.ts use
  // upstream. A hand-rolled copy here re-parsed the entries those callers had just
  // normalized and silently dropped focusActionType and campaignId, unbinding
  // campaign conversations on every DTO round-trip.
  const parsed: ChatSubjectFocusEntry[] = [];
  value.forEach((entry) => {
    if (!isRecord(entry)) return;
    const normalized = normalizeSubjectFocus(entry);
    const focusedAt = normalizeNonEmptyString(entry.focusedAt);
    if (!normalized || !focusedAt) return;
    parsed.push(toSubjectFocusHistoryEntry(normalized, focusedAt));
  });

  return parsed.length > 0 ? parsed : undefined;
};

export const resolveProjectIdFromChatSessionContext = (
  session: ChatSessionProjectLike | null | undefined
): string | null => {
  if (!session) return null;

  const fromActiveSubject = resolveProjectIdFromProjectSubject(session.activeSubject);
  if (fromActiveSubject) return fromActiveSubject;

  const history = normalizeSubjectHistory(session.subjectHistory);
  if (history && history.length > 0) {
    let fallbackProjectId: string | null = null;
    let latestFocusedAtMs = Number.NEGATIVE_INFINITY;

    history.forEach((entry) => {
      if (entry.entityType !== 'Project') return;
      if (!isEntityFocusSubject(entry as ActiveEntityFocus)) return;
      fallbackProjectId = fallbackProjectId ?? entry.entityId;

      const focusedAtMs = Date.parse(entry.focusedAt);
      if (!Number.isNaN(focusedAtMs) && focusedAtMs >= latestFocusedAtMs) {
        latestFocusedAtMs = focusedAtMs;
        fallbackProjectId = entry.entityId;
      }
    });

    if (fallbackProjectId) return fallbackProjectId;
  }

  const fromLegacyProjectId = normalizeNonEmptyString(session.projectId);
  if (fromLegacyProjectId) return fromLegacyProjectId;

  // Last resort: the session's immutable studio home. Only trust the scope's
  // entity id when the module is studio and the entity is a Project.
  if (
    session.creationModule === 'studio' &&
    (session.creationEntityType === 'Project' || session.creationEntityType == null)
  ) {
    return normalizeNonEmptyString(session.creationEntityId);
  }

  return null;
};

export const selectActiveSessionProjectIdFromInventoryState = (
  state: {
    activeChatSessionId?: unknown;
    inventory?: {
      ChatSession?: {
        registry?: Record<string, ChatSessionProjectLike | undefined>;
      };
    };
  }
): string | null => {
  const activeSessionId = normalizeNonEmptyString(state.activeChatSessionId);
  if (!activeSessionId) return null;
  const session = state.inventory?.ChatSession?.registry?.[activeSessionId];
  return resolveProjectIdFromChatSessionContext(session);
};

const upsertProjectHistoryEntry = (
  history: ChatSubjectFocusEntry[] | undefined,
  projectId: string,
  focusedAt: string
): ChatSubjectFocusEntry[] => {
  const next = Array.isArray(history) ? [...history] : [];
  const existingIndex = next.findIndex(
    (entry) => entry.entityType === 'Project' && entry.entityId === projectId
  );

  if (existingIndex >= 0) {
    next[existingIndex] = {
      entityType: 'Project',
      entityId: projectId,
      focusedAt,
    };
    return next;
  }

  next.push({
    entityType: 'Project',
    entityId: projectId,
    focusedAt,
  });
  return next;
};

export const migrateLegacySessionProjectId = (params: {
  activeSubject?: unknown;
  subjectHistory?: unknown;
  projectId?: unknown;
  updatedAt?: unknown;
}): {
  activeSubject?: ActiveEntityFocus | null;
  subjectHistory?: ChatSubjectFocusEntry[] | undefined;
} => {
  const activeSubject = coerceActiveSubject(params.activeSubject);
  const subjectHistory = normalizeSubjectHistory(params.subjectHistory);
  const legacyProjectId = normalizeNonEmptyString(params.projectId);

  if (!legacyProjectId) {
    return {
      activeSubject: activeSubject ?? undefined,
      subjectHistory,
    };
  }

  const activeProjectId = resolveProjectIdFromProjectSubject(activeSubject);
  const hasProjectInHistory = (subjectHistory ?? []).some((entry) => (
    entry.entityType === 'Project' && isEntityFocusSubject(entry as ActiveEntityFocus)
  ));
  const focusedAt = normalizeNonEmptyString(params.updatedAt) ?? new Date().toISOString();

  const nextHistory = hasProjectInHistory
    ? subjectHistory
    : upsertProjectHistoryEntry(subjectHistory, legacyProjectId, focusedAt);

  const nextActiveSubject = activeProjectId
    ? activeSubject
    : activeSubject ?? { entityType: 'Project', entityId: legacyProjectId };

  return {
    activeSubject: nextActiveSubject,
    subjectHistory: nextHistory,
  };
};
